import React from 'react';
import { ArrowRight, Play, Clock, Mail, Filter, UserCheck } from 'lucide-react';
import { WorkflowDefinition } from '../../types/workflows';

interface WorkflowPreviewProps {
  workflow: WorkflowDefinition;
}

export const WorkflowPreview: React.FC<WorkflowPreviewProps> = ({ workflow }) => {
  const ACTIVITY_ICONS = {
    FetchLeadsActivity: <Filter className="w-5 h-5 text-blue-600" />,
    EmailEnrichmentActivity: <Mail className="w-5 h-5 text-green-600" />,
    EmailSendingActivity: <Mail className="w-5 h-5 text-green-600" />,
    LinkedInEnrichmentActivity: <UserCheck className="w-5 h-5 text-blue-600" />,
    EmailReplyWaitActivity: <Clock className="w-5 h-5 text-yellow-600" />,
    default: <Play className="w-5 h-5 text-gray-600" />
  };

  const getNodeIcon = (nodeType: string) => {
    return ACTIVITY_ICONS[nodeType as keyof typeof ACTIVITY_ICONS] || ACTIVITY_ICONS.default;
  };

  const formatNodeName = (nodeType: string) => {
    return nodeType.replace('Activity', '').replace(/([A-Z])/g, ' $1').trim();
  };

  const nodes = workflow.graph?.nodes || [];
  const edges = workflow.graph?.edges || [];

  return (
    <div className="h-full overflow-auto p-6">
      <div className="max-w-4xl mx-auto">
        {/* Workflow Header */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{workflow.name}</h3>
          <p className="text-gray-600 mb-4">{workflow.description}</p>
          
          <div className="flex items-center space-x-6 text-sm text-gray-500">
            <div>
              <span className="font-medium">Trigger:</span> {workflow.trigger_type}
            </div>
            {workflow.trigger_config?.cron && (
              <div>
                <span className="font-medium">Schedule:</span> {workflow.trigger_config.cron}
              </div>
            )}
            {workflow.trigger_config?.interval_seconds && (
              <div>
                <span className="font-medium">Interval:</span> Every {Math.floor(workflow.trigger_config.interval_seconds / 60)} minutes
              </div>
            )}
          </div>
        </div>

        {/* Workflow Steps */}
        <div className="space-y-4">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Workflow Steps</h4>
          
          {nodes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No workflow steps defined yet.
            </div>
          ) : (
            <div className="space-y-6">
              {nodes.map((node, index) => (
                <div key={node.node_id} className="flex items-start space-x-4">
                  {/* Step Number */}
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </div>

                  {/* Step Content */}
                  <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-2">
                      {getNodeIcon(node.config?.activity_name || node.type)}
                      <h5 className="font-medium text-gray-900">
                        {node.name || formatNodeName(node.config?.activity_name || node.type)}
                      </h5>
                    </div>
                    
                    {/* Node Parameters */}
                    {node.config?.parameters && Object.keys(node.config.parameters).length > 0 && (
                      <div className="mt-3">
                        <h6 className="text-sm font-medium text-gray-700 mb-2">Configuration:</h6>
                        <div className="bg-gray-50 rounded p-3 text-sm">
                          <pre className="whitespace-pre-wrap text-gray-600">
                            {JSON.stringify(node.config.parameters, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* Conditions for this step */}
                    {edges.some(edge => edge.source_node_id === node.node_id) && (
                      <div className="mt-3">
                        <h6 className="text-sm font-medium text-gray-700 mb-2">Next Steps:</h6>
                        <div className="space-y-1">
                          {edges
                            .filter(edge => edge.source_node_id === node.node_id)
                            .map((edge, edgeIndex) => {
                              const targetNode = nodes.find(n => n.node_id === edge.destination_node_id);
                              return (
                                <div key={edgeIndex} className="flex items-center text-sm text-gray-600">
                                  <ArrowRight className="w-3 h-3 mr-2" />
                                  <span>
                                    If <strong>{edge.condition?.type || 'always'}</strong>, go to{' '}
                                    <strong>{targetNode?.name || targetNode?.node_id}</strong>
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Workflow Summary */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{nodes.length}</div>
              <div className="text-sm text-blue-800">Nodes</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{edges.length}</div>
              <div className="text-sm text-green-800">Connections</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-600">{workflow.trigger_type}</div>
              <div className="text-sm text-purple-800">Trigger Type</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};